import { useState } from "react";
import { Check, ChevronDown, Stethoscope, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { getDentistColor, getDentistTitle } from "@/lib/selected-dentists";
import { useEmAtendimento } from "@/lib/em-atendimento";
import { SeloEmAtendimento } from "@/components/shared/SeloEmAtendimento";

type Dentist = {
  id: string;
  name: string;
  image: string | null;
};

type Props = {
  selectedIds: string[];
  onToggle: (id: string) => void;
  onSelectAll: (ids: string[]) => void;
  onClear: () => void;
  dentistSettings: Record<string, { color?: string; title?: string }>;
};

/** Lista inline — usada quando a sidebar da agenda está aberta/fixada. */
export function DentistCheckboxList({
  selectedIds,
  onToggle,
  onSelectAll,
  onClear,
  dentistSettings,
}: Props) {
  const query = trpc.organization.listDentists.useQuery(undefined, {
    staleTime: 60_000,
  });
  const dentists = (query.data ?? []) as Dentist[];
  const { porDentista } = useEmAtendimento();

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 px-0.5 py-1 text-xs italic text-zinc-400 dark:text-muted-foreground">
        <Stethoscope className="h-3.5 w-3.5" />
        Carregando...
      </div>
    );
  }

  if (dentists.length === 0) {
    return (
      <div className="flex items-center gap-2 px-0.5 py-1 text-xs italic text-zinc-500 dark:text-muted-foreground">
        <Stethoscope className="h-3.5 w-3.5" />
        Nenhum profissional
      </div>
    );
  }

  const allSelected = dentists.every((d) => selectedIds.includes(d.id));

  return (
    <div className="space-y-0.5">
      {dentists.map((d) => {
        const checked = selectedIds.includes(d.id);
        const color = getDentistColor(d.id, dentistSettings);
        const title = getDentistTitle(d.id, dentistSettings);
        const firstName = d.name?.split(/\s+/)[0] ?? "?";
        return (
          <button
            key={d.id}
            type="button"
            onClick={() => onToggle(d.id)}
            className={cn(
              "group flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left text-[12px] transition-colors",
              checked
                ? "bg-zinc-50 dark:bg-muted/30"
                : "hover:bg-zinc-50 dark:hover:bg-muted/30",
            )}
          >
            <Checkbox checked={checked} color={color} />
            <DentistAvatar dentist={d} color={color} size={22} />
            <span
              className={cn(
                "min-w-0 flex-1 truncate",
                checked
                  ? "font-semibold text-zinc-900 dark:text-foreground"
                  : "text-zinc-600 dark:text-muted-foreground",
              )}
            >
              {title} {firstName}
            </span>
            {porDentista.get(d.id) && <SeloEmAtendimento atendimento={porDentista.get(d.id)!} compacto />}
          </button>
        );
      })}

      {dentists.length > 1 && (
        <div className="mt-1 flex gap-1 px-0.5 pt-1">
          <button
            type="button"
            onClick={() =>
              allSelected ? onClear() : onSelectAll(dentists.map((d) => d.id))
            }
            className="flex-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-brand-700 transition-colors hover:bg-brand-50 dark:text-primary dark:hover:bg-primary/10"
          >
            {allSelected ? "Desmarcar todos" : "Selecionar todos"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Botão dropdown — usado quando a sidebar está oculta (topbar). */
export function DentistDropdownButton({
  selectedIds,
  onToggle,
  onSelectAll,
  onClear,
  dentistSettings,
}: Props) {
  const [open, setOpen] = useState(false);
  const query = trpc.organization.listDentists.useQuery(undefined, {
    staleTime: 60_000,
  });
  const dentists = (query.data ?? []) as Dentist[];
  const total = dentists.length;
  const selected = dentists.filter((d) => selectedIds.includes(d.id));
  const allSelected = total > 0 && selected.length === total;
  const none = selected.length === 0;

  const buttonLabel = none
    ? "Nenhum"
    : allSelected
      ? "Todos"
      : selected.length === 1
        ? (() => {
            const d = selected[0];
            const title = getDentistTitle(d.id, dentistSettings);
            return `${title} ${d.name?.split(/\s+/)[0]}`;
          })()
        : `${selected.length} profissionais`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Filtrar profissionais"
          className={cn(
            "inline-flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors",
            none
              ? "border-amber-300 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
              : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted",
          )}
        >
          {/* Avatar miniatura dos selecionados (até 3) */}
          {selected.length > 0 && selected.length <= 3 ? (
            <div className="flex -space-x-1">
              {selected.slice(0, 3).map((d) => {
                const color = getDentistColor(d.id, dentistSettings);
                return (
                  <DentistAvatar
                    key={d.id}
                    dentist={d}
                    color={color}
                    size={18}
                    ringColor="white"
                  />
                );
              })}
            </div>
          ) : (
            <Users className="h-3.5 w-3.5" />
          )}
          <span className="hidden sm:inline">{buttonLabel}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-64 border border-zinc-200 bg-white p-2 shadow-xl dark:border-border dark:bg-card"
      >
        <div className="mb-2 flex items-center justify-between px-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
            Profissionais
          </p>
          <span className="text-[10px] text-zinc-400 dark:text-muted-foreground/70">
            {selected.length}/{total}
          </span>
        </div>

        <DentistCheckboxList
          selectedIds={selectedIds}
          onToggle={onToggle}
          onSelectAll={onSelectAll}
          onClear={onClear}
          dentistSettings={dentistSettings}
        />
      </PopoverContent>
    </Popover>
  );
}

// ─── Primitivas ────────────────────────────────────────────

function Checkbox({ checked, color }: { checked: boolean; color: string }) {
  return (
    <span
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-all",
        checked
          ? "border-transparent"
          : "border-zinc-300 bg-white dark:border-border dark:bg-background",
      )}
      style={checked ? { backgroundColor: color, borderColor: color } : undefined}
    >
      {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
    </span>
  );
}

function DentistAvatar({
  dentist,
  color,
  size = 24,
  ringColor,
}: {
  dentist: Dentist;
  color: string;
  size?: number;
  ringColor?: string;
}) {
  const initial = dentist.name?.charAt(0).toUpperCase() ?? "?";
  const ring = ringColor ? `2px solid ${ringColor}` : undefined;
  if (dentist.image) {
    return (
      <img
        src={dentist.image}
        alt={dentist.name}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size, border: ring }}
      />
    );
  }
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        fontSize: Math.round(size * 0.42),
        border: ring,
      }}
    >
      {initial}
    </span>
  );
}
